// @ts-strict-ignore
import { ThrowResultError } from './ResultError';
import type { MatchBadInput } from './types/MatchBadInput';
import type { MatchBadResult } from './types/MatchBadResult';
import type { MatchBadWithDefaultResult } from './types/MatchBadWithDefaultResult';
import type { MatchBadWithDefaultInput } from './types/MatchBadWithDefaultInput';
import type { OkResult } from './OkResult';

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export type GenericResultType<TValue = any, TReason = any> =
  | {
      success: true;
      hasValue: false;
    }
  | {
      success: true;
      hasValue: true;
      value: TValue;
    }
  | {
      success: false;
      hasValue: false;
      reason: TReason;
    }
  | {
      success: false;
      hasValue: true;
      reason: TReason;
      value: TValue;
    };

export type KeepSuccessValue<T extends GenericResultType> = T extends {
  success: true;
  hasValue: true;
  value: unknown;
}
  ? T['value'] extends undefined
    ? never
    : T['value']
  : never;

export type KeepUnsuccessResult<T extends GenericResultType> = T extends {
  success: false;
  hasValue: true;
  value: unknown;
}
  ? T
  : never;

export type KeepUnsuccessValue<T extends GenericResultType> = T extends {
  success: false;
  hasValue: true;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  value: any;
}
  ? T['value']
  : T extends {
        success: false;
        hasValue: false;
      }
    ? void
    : never;

export type KeepUnsuccessReason<T extends GenericResultType> = T extends {
  success: false;
}
  ? T['reason']
  : never;

export class Result {
  static unwrapOnlyOk<T extends OkResult>(result: T): T {
    if (!result.success) {
      ThrowResultError(result);
    }

    return result.hasValue ? result.value : void 0;
  }

  static unwrap<T extends GenericResultType>(result: T): KeepSuccessValue<T> {
    if (!result.success) {
      ThrowResultError(result);
    }

    return result.hasValue ? result.value : void 0;
  }

  static unwrapBad<T extends GenericResultType>(result: T): KeepUnsuccessResult<T> {
    if (result.success) {
      ThrowResultError(result);
    }

    return result as KeepUnsuccessResult<T>;
  }

  static unwrapBadReason<T extends GenericResultType>(result: T): KeepUnsuccessReason<T> {
    if (result.success) {
      ThrowResultError(result);
    }

    return Result.unwrapBad(result).reason;
  }

  static unwrapBadValue<T extends GenericResultType>(result: T): KeepUnsuccessValue<T> {
    const unwrap = Result.unwrapBad(result);

    return unwrap.hasValue ? unwrap.value : void 0;
  }

  static matchBad<T extends GenericResultType, TMap extends MatchBadInput<T>>(
    result: T,
    map: TMap
  ): MatchBadResult<T, TMap> {
    if (result.success === false) {
      const key = result.reason;
      const entry = map[key];

      if (!entry) {
        ThrowResultError(result);
      }

      return entry(result);
    }
    // Weird behaviour without this line: would return void even though you provide BadResult.
    return undefined as MatchBadResult<T, TMap>;
  }

  // todo merge withdefault with simple matchBad, if default is defined, all map properties can be optional
  static matchBadWithDefault<T extends GenericResultType, TMap extends MatchBadWithDefaultInput<T>>(
    result: T,
    map: TMap
  ): MatchBadWithDefaultResult<T, TMap> {
    if (result.success === false) {
      const key = result.reason;
      const entry = map[key] ?? map['default'];

      if (!entry) {
        ThrowResultError(result);
      }

      return entry(result);
    }
  }
}
